import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { take } from 'rxjs';
import { PageHeaderComponent } from '../page-header/page-header.component';
import { PageFooterComponent } from '../page-footer/page-footer.component';
import { NotesComponent } from '../notes/notes.component';
import { ProductDemosComponent } from '../product-demos/product-demos.component';
import { WorkflowDemosComponent } from '../workflow-demos/workflow-demos.component';
import { UseCasesComponent } from '../use-cases/use-cases.component';
import { BookmarksCollectionComponent } from '../bookmarks-collection/bookmarks-collection.component';
import { SpacesService } from '../../services/spaces.service';
import { SolutionsService } from '../../services/solutions.service';
import { SpaceConfigOptionsService } from '../../services/space-config-options.service';
import { DemoFilters, defaultDemoFilters } from '../../models/demo-filters.model';

@Component({
  selector: 'app-demo-and-proofs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    PageHeaderComponent,
    PageFooterComponent,
    NotesComponent,
    ProductDemosComponent,
    WorkflowDemosComponent,
    UseCasesComponent,
    BookmarksCollectionComponent
  ],
  templateUrl: './demo-and-proofs.component.html',
  styleUrl: './demo-and-proofs.component.scss'
})
export class DemoAndProofsComponent implements OnInit {
  @Input() showNotes = false;
  selectedSolutionNames: string[] = [];
  filters: DemoFilters = defaultDemoFilters();
  pendingFilters: DemoFilters = defaultDemoFilters();
  activeDropdown: string | null = null;
  bookmarkedProductDemos: number[] = [];
  bookmarkedWorkflowDemos: number[] = [];
  bookmarkedUseCases: number[] = [];
  spaceName = '';
  private productFilterOptions: string[] = [];

  constructor(
    private readonly spacesService: SpacesService,
    private readonly solutionsService: SolutionsService,
    private readonly spaceConfigOptions: SpaceConfigOptionsService
  ) {}

  ngOnInit(): void {
    this.spacesService.selectedSolutionNames$.subscribe((solutionNames) => {
      this.selectedSolutionNames = solutionNames;
    });

    this.spacesService.activeSpace$.subscribe((space) => {
      this.bookmarkedProductDemos = space.bookmarkedProductDemos ?? [];
      this.bookmarkedWorkflowDemos = space.bookmarkedWorkflowDemos ?? [];
      this.bookmarkedUseCases = space.bookmarkedUseCases ?? [];
      this.spaceName = space.name ?? '';
    });

    this.solutionsService.getSolutions().subscribe((solutions) => {
      this.productFilterOptions = solutions.map(s => s.name);
      this.prefillFiltersFromActiveSpace();
    });
  }

  onBookmarkToggleProductDemo(id: number): void {
    this.spacesService.toggleBookmark('bookmarkedProductDemos', id);
  }

  onBookmarkToggleWorkflowDemo(id: number): void {
    this.spacesService.toggleBookmark('bookmarkedWorkflowDemos', id);
  }

  onBookmarkToggleUseCase(id: number): void {
    this.spacesService.toggleBookmark('bookmarkedUseCases', id);
  }

  private prefillFiltersFromActiveSpace(): void {
    this.spacesService.activeSpace$.pipe(take(1)).subscribe((space) => {
      const selectedSolutions = this.spacesService.getSelectedSolutions();
      const hasData = (space.relevantIndustries?.length ?? 0) > 0
        || (space.disciplines?.length ?? 0) > 0
        || (space.relevantMaterials?.length ?? 0) > 0
        || selectedSolutions.length > 0;

      if (!hasData) return;

      const productPrefill = selectedSolutions;

      const prefilled: DemoFilters = {
        ...defaultDemoFilters(),
        industry: [...(space.relevantIndustries ?? [])],
        discipline: [...(space.disciplines ?? [])],
        material: [...(space.relevantMaterials ?? [])],
        product: productPrefill
      };

      this.filters = prefilled;
      this.pendingFilters = { ...prefilled, industry: [...prefilled.industry], discipline: [...prefilled.discipline], material: [...prefilled.material], product: [...prefilled.product] };
    });
  }

  getFilterOptions(filterType: 'product' | 'industry' | 'discipline' | 'material'): string[] {
    if (filterType === 'product') {
      return this.productFilterOptions;
    }
    const map: Record<string, string[]> = {
      industry: this.spaceConfigOptions.industries.map(i => i.id),
      discipline: this.spaceConfigOptions.disciplines.map(d => d.id),
      material: this.spaceConfigOptions.materials.map(m => m.id)
    };
    return map[filterType] ?? [];
  }

  getFilterLabel(filterType: 'industry' | 'discipline' | 'material', id: string): string {
    switch (filterType) {
      case 'industry': return this.spaceConfigOptions.industries.find(i => i.id === id)?.name ?? id;
      case 'discipline': return this.spaceConfigOptions.disciplines.find(d => d.id === id)?.name ?? id;
      case 'material': return this.spaceConfigOptions.materials.find(m => m.id === id)?.name ?? id;
    }
  }

  onSearchChange(): void {
    this.filters = { ...this.filters };
  }

  applyDropdown(type: keyof Omit<DemoFilters, 'searchText'>): void {
    this.filters = { ...this.filters, [type]: [...this.pendingFilters[type]] };
    this.activeDropdown = null;
  }

  resetDropdown(type: keyof Omit<DemoFilters, 'searchText'>): void {
    this.pendingFilters = { ...this.pendingFilters, [type]: [] };
  }

  togglePending(type: keyof Omit<DemoFilters, 'searchText'>, value: string): void {
    const current = this.pendingFilters[type] as string[];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    this.pendingFilters = { ...this.pendingFilters, [type]: updated };
  }

  isPendingSelected(type: keyof Omit<DemoFilters, 'searchText'>, value: string): boolean {
    return (this.pendingFilters[type] as string[]).includes(value);
  }

  toggleDropdown(dropdown: string): void {
    if (this.activeDropdown === dropdown) {
      this.activeDropdown = null;
    } else {
      const key = dropdown as keyof Omit<DemoFilters, 'searchText'>;
      this.pendingFilters = { ...this.pendingFilters, [key]: [...this.filters[key]] };
      this.activeDropdown = dropdown;
    }
  }

  resetAll(): void {
    this.spacesService.clearActiveSpace();
    this.filters = defaultDemoFilters();
    this.activeDropdown = null;
  }
}
